import React, { useState, useEffect } from 'react'
import { utilService } from '../services/utilService'
const { Dropdown } = require('monday-ui-react-core')
interface CustomFieldsProps {
  isEditable: boolean
  boardId: string | null
  mappingCustomFields: any[]
  boardColumns: DropDownOption[]
  onSetMapping?: Function
  onSetChildItem?: Function
  childName?: string
  setUnfilledRequiredCustom?: React.Dispatch<React.SetStateAction<string[]>>
}

export const CustomFields: React.FC<CustomFieldsProps> = ({ isEditable, boardId, mappingCustomFields, boardColumns, onSetMapping, onSetChildItem, childName, setUnfilledRequiredCustom }) => {
  const [isFieldChanged, setIsFieldChanged] = useState<boolean>(false)
  const [customFields, setCustomFields] = useState<CustomField[]>([])

  useEffect(() => {
    loadSections()
  }, [isFieldChanged])

  /**
   * This useEffect is copying the mappingCustomFields to a new state ONLY for tracking the unfilled mendatory fields.
   */
  useEffect(() => {
    setUnfilledRequiredCustom && setCustomFields(mappingCustomFields)

    return () => setUnfilledRequiredCustom && setUnfilledRequiredCustom([])
  }, [mappingCustomFields])

  useEffect(() => {
    setUnfilledRequiredCustom && getUnfilledRequiredCustom(customFields)
  }, [customFields])

  /**
   * if there is a section (dynamic section) -> get the section -> make it as custom field -> add to the current custom field the section as currSection.
   */
  const loadSections = () => {
    mappingCustomFields.forEach((field, idx) => {
      if (field.sections.length) {
        const section = field.sections.find((section: any) => section.name === field.label)
        if (section) {
          const mappedCustomField = utilService.mappingCustomFields(section.fields)
          mappedCustomField[0].fatherIdx = idx
          if (mappingCustomFields[idx].currSection) {
            mappedCustomField[0].value = mappingCustomFields[idx].currSection[0].value
            mappedCustomField[0].label = mappingCustomFields[idx].currSection[0].label
          }
          mappingCustomFields[idx].currSection = mappedCustomField
          onSetMapping && onSetMapping('customFields', mappingCustomFields)
          onSetChildItem && onSetChildItem(mappingCustomFields)
        } else {
          mappingCustomFields[idx].currSection = null
          onSetMapping && onSetMapping('customFields', mappingCustomFields)
          onSetChildItem && onSetChildItem(mappingCustomFields)
        }
      }
    })
  }

  /**
   * The function checks the unfilled mendatory fields and dispatch it to the "Mapping" component to check if all required fields are filled while saving.
   */
  const getUnfilledRequiredCustom = (customFields: CustomField[]) => {
    const mappedUnfilledCustom = customFields.filter((field: CustomField) => !field.value && field.isRequired).map((field: CustomField) => field.name)
    setUnfilledRequiredCustom && setUnfilledRequiredCustom(mappedUnfilledCustom)
  }

  const onSetCustomFields = (option: ExtendedDDOption, idx: number, isSection: boolean): void => {
    setIsFieldChanged((isFieldChanged) => !isFieldChanged)
    if (isSection) {
      const copyMappingCustomFields = JSON.parse(JSON.stringify(mappingCustomFields))
      copyMappingCustomFields[option.fatherIdx!].currSection[0].value = option.value
      copyMappingCustomFields[option.fatherIdx!].currSection[0].label = option.label
      if (process.env.NODE_ENV !== 'production') console.log('file: CustomFields.tsx -> line 65 -> onSetCustomFields -> copyMappingCustomFields[option.fatherIdx!].currSection[0]', copyMappingCustomFields[option.fatherIdx!].currSection[0])
      onSetMapping && onSetMapping('customFields', copyMappingCustomFields)
      if (process.env.NODE_ENV !== 'production') console.log('onSetChildItem', onSetChildItem)
      onSetChildItem && onSetChildItem(copyMappingCustomFields)
    } else {
      if (!option) {
        mappingCustomFields[idx].value = null
        mappingCustomFields[idx].label = null
      } else {
        mappingCustomFields[idx].value = option.value
        mappingCustomFields[idx].label = option.label
      }
      if (process.env.NODE_ENV !== 'production') console.log('file: CustomFields.tsx:84 -> mappingCustomFields', mappingCustomFields)
      setCustomFields(JSON.parse(JSON.stringify(mappingCustomFields)))
      onSetMapping && onSetMapping('customFields', mappingCustomFields)
      onSetChildItem && onSetChildItem(mappingCustomFields)
    }
  }

  const customFieldsOptions = (field: CustomField) => {

    if(!boardColumns?.length) return

    if (field.choices?.length) {
      const mappedChoices = field.choices.map((choice: string[]) => {
        return { ...field, label: choice[0], value: choice[1] }
      })
      return mappedChoices
    } else {
      const mappedBoardColumns = boardColumns.map((column: DropDownOption) => {
        return { ...field, ...column }
      })
      if (field.type === 'custom_date') return mappedBoardColumns.filter((column) => column.type === 'date') // if custom field is date, column type must be date too
      else if (field.type === 'custom_checkbox') return mappedBoardColumns.filter((column) => column.type === 'checkbox') // if custom field is checbkox, column type must be checbkox too
      else if (field.type === 'custom_number' || field.type === 'custom_decimal') return mappedBoardColumns.filter((column) => column.type === 'numbers') // if custom field is numbers, column type must be numbers too
      else if (field.type === 'custom_url') return mappedBoardColumns.filter((column) => column.type === 'link') // if custom field is link, column type must be link too
      return mappedBoardColumns
    }
  }

  const GetCustomField: React.FC<{ field: any; idx: number }> = ({ field, idx }) => {
    switch (field.type) {
      case 'custom_dropdown':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'No options'}
            placeholder={field.choices.length ? 'Choose from options' : 'Choose column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_text':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'No options'}
            placeholder={field.choices.length ? 'Choose from options' : 'Choose column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_date':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'Must be date column type'}
            placeholder={'Choose date column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'nested_field':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'No options'}
            placeholder={field.choices.length ? 'Choose from options' : 'Choose column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_number':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'Must be number column type'}
            placeholder={'Choose number column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_decimal':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'Must be number column type'}
            placeholder={'Choose number column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_checkbox':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'Must be checkbox column type'}
            placeholder={'Choose checkbox column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_url':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'Must be link column type'}
            placeholder={'Choose link column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
      case 'custom_multi_lookup':
        return '"Multi Dropdown" is not supported.'
      case 'custom_static_rich_text':
        return field.name
      default:
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!boardId || !isEditable}
            noOptionsMessage={() => 'No options'}
            placeholder={field.choices.length ? 'Choose from options' : 'Choose column'}
            onChange={(option: DropDownOption) => onSetCustomFields(option, idx, false)}
            options={customFieldsOptions(field)}
            value={field.value && mappingCustomFields[idx]}
          />
        )
    }
  }

  return (
    <div className="dropdown-container">
      <h2>Custom Fields {childName && '(' + childName + ')'}</h2>
      {mappingCustomFields.map((field: any, idx: number) => (
        <div key={idx}>
          <label>
            <p>
              {field.type === 'custom_static_rich_text' ? field.fieldOptions?.content_name : field.name}: <span>{field.isRequired && '*'}</span>
            </p>
            <GetCustomField field={field} idx={idx} />
          </label>
          {field.currSection &&
            field.currSection.map((field: any, idx: number) => (
              <label key={idx}>
                <p>
                  {field.name}: <span>{field.isRequired && '*'}</span>
                </p>
                <Dropdown
                  className="dropdown-wrapper"
                  disabled={!boardId || !isEditable}
                  noOptionsMessage={() => (field.type === 'custom_date' ? 'Must be date column type' : 'No options')}
                  placeholder={field.choices.length ? 'Choose from options' : 'Choose column'}
                  onChange={(option: DropDownOption) => onSetCustomFields(option, idx, true)}
                  options={customFieldsOptions(field)}
                  value={field.value && mappingCustomFields[field.fatherIdx].currSection[0]}
                />
              </label>
            ))}
        </div>
      ))}
    </div>
  )
}
